import type { CvssVersion } from './cvss-enums';

/**
 * Represents a CVSS score produced by an instance of {@link CvssScoringEngine}.
 *
 * @public
 */
export class CvssScore {
    private _supported: boolean;

    /**
     * Initializes a new instance of a CVSS score.
     *
     * @param baseScore - the base score
     * @param impactSubscore - the impact subscore
     * @param exploitabilitySubscore - the exploitability subscore
     * @param temporalScore - the temporal score
     * @param environmentalScore - the environmental score
     * @param modifiedImpactSubscore - the modified impact score
     * @param overallScore - the overall score (non-standard addition by NIST/NVD)
     * @param threatScore - the threat score
     * @param supported - the score is supported by the CVSS version used to compute it
     */
    public constructor(
        version: CvssVersion,
        baseScore = 0, // All CVSS
        impactSubscore: number | null = null, // CVSS v2, v3, v3.1
        exploitabilitySubscore: number | null = 0, // CVSS v2, v3, v3.1
        temporalScore: number | null = null, // CVSS v2, v3, v3.1
        environmentalScore: number | null = null, // All CVSS
        modifiedImpactSubscore: number | null = null, // CVSS v2, v3, v3.1
        overallScore = 0, // All CVSS
        threatScore: number | null = null, // CVSS v4
        supported = true,
    ) {
        this._baseScore = baseScore;
        this._impactSubscore = impactSubscore;
        this._exploitabilitySubscore = exploitabilitySubscore;
        this._temporalScore = temporalScore;
        this._environmentalScore = environmentalScore;
        this._modifiedImpactSubscore = modifiedImpactSubscore;
        this._overallScore = overallScore;
        this._threatScore = threatScore;

        this._version = version;
        this._supported = supported;
    }

    private _version: CvssVersion;
    /**
     * Gets the CVSS version used to compute this score.
     *
     * @returns the CVSS version
     */
    get version(): CvssVersion {
        return this._version;
    }

    private _baseScore: number;

    /**
     * Gets the base score.
     *
     * The base score represents the severity of the vulnerability based on its intrinsic properties. It is constant
     * over time.
     *
     * @returns the base score
     */
    get baseScore(): number {
        return this._baseScore;
    }

    private _impactSubscore: number | null;

    /**
     * Gets the impact subscore.
     *
     * The impact subscore is a measure of impact, derived from the base impact metrics.
     *
     * @returns the impact subscore
     */
    get impactSubscore(): number | null {
        return this._impactSubscore;
    }

    private _exploitabilitySubscore: number | null;

    /**
     * Gets the exploitability subscore.
     *
     * The exploitability subscore is a measure of exploitability, derived from the base exploitability metrics.
     *
     * @returns the exploitability subscore
     */
    get exploitabilitySubscore(): number | null {
        return this._exploitabilitySubscore;
    }

    private _temporalScore: number | null;

    /**
     * Gets the temporal score.
     *
     * The temporal score represents the severity of the vulnerability as it currently exists, accounting for factors
     * such as whether or not there exists an exploit, patch or workaround for the vulnerability.
     *
     * @returns the temporal score
     */
    get temporalScore(): number | null {
        return this._temporalScore;
    }

    private _environmentalScore: number | null;

    /**
     * Gets the environmental score.
     *
     * The environmental score represents the severity of the vulnerability as it exists on a specific organization's
     * IT infrastructure.
     *
     * @returns the environmental score
     */
    get environmentalScore(): number | null {
        return this._environmentalScore;
    }

    private _modifiedImpactSubscore: number | null;

    /**
     * Gets the modified impact subscore.
     *
     * The impact subscore is a measure of impact, derived from the modified (environmental) impact metrics.
     *
     * @returns the moodified impact subscore
     */
    get modifiedImpactSubscore(): number | null {
        return this._modifiedImpactSubscore;
    }

    private _overallScore: number;

    /**
     * Gets the overall score.
     *
     * The overall score is a non-standard measure which is preferentially equal to the environmental score, then
     * temporal score and finally base score if neither of the former two are defined.
     *
     * @returns the overall score
     */
    get overallScore(): number {
        return this._overallScore;
    }

    private _threatScore: number | null;
    /**
     * Gets the threat score.
     *
     * The threat score is a measure of the threat posed by the vulnerability, used in CVSS v4.
     *
     * @returns the threat score
     */
    get threatScore(): number | null {
        return this._threatScore;
    }

    /**
     * Gets whether the score is supported by the CVSS version used to compute it.
     *
     * @returns true if the score is supported, otherwise false
     */

    get isSupported(): boolean {
        return this._supported;
    }
}
